export interface PDFExportOptions {
  scale?: number;
  backgroundColor?: string;
  filename?: string;
  margin?: number;
  pageSize?: 'a4' | 'letter' | 'receipt';
}

// Helper function to prepare receipt for export
function prepareReceiptForExport(clonedDoc: Document, element: HTMLElement) {
  const receiptPaper = clonedDoc.querySelector('[class*="receipt-paper"]') as HTMLElement;
  if (receiptPaper) {
    // Ensure fonts are loaded
    receiptPaper.style.fontFamily = getComputedStyle(element).fontFamily;
    // Keep overflow hidden to contain dividers, but add extra padding
    // to prevent sub-pixel clipping at the edges
    receiptPaper.style.paddingLeft = '16px';
    receiptPaper.style.paddingRight = '16px';
    
    // Preserve barcode SVG text styling - html2canvas can lose SVG text font-size
    const barcodeSvgs = receiptPaper.querySelectorAll('.barcode-container svg');
    barcodeSvgs.forEach((svg) => {
      const textElements = svg.querySelectorAll('text');
      textElements.forEach((text) => {
        // Get computed font-size and set it as inline style to preserve it
        const computedStyle = getComputedStyle(text);
        (text as SVGTextElement).style.fontSize = computedStyle.fontSize;
      });
    });

  }
}

/**
 * Export a DOM element to PDF
 */
export async function exportToPDF(
  element: HTMLElement,
  options: PDFExportOptions = {}
): Promise<Blob> {
  const {
    scale = 2,
    backgroundColor = 'transparent',
    margin = 0,
    pageSize = 'receipt',
  } = options;

  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor,
    logging: false,
    useCORS: true,
    allowTaint: true,
    removeContainer: true,
    imageTimeout: 15000,
    onclone: (clonedDoc) => prepareReceiptForExport(clonedDoc, element),
  });

  const imgData = canvas.toDataURL('image/png', 1.0);
  const imgWidth = canvas.width / scale;
  const imgHeight = canvas.height / scale;

  // Calculate PDF dimensions
  let pdfWidth: number;
  let pdfHeight: number;
  let orientation: 'portrait' | 'landscape' = 'portrait';

  if (pageSize === 'receipt') {
    // For receipt, use the actual image dimensions plus margins
    pdfWidth = imgWidth + margin * 2;
    pdfHeight = imgHeight + margin * 2;
  } else if (pageSize === 'a4') {
    pdfWidth = 210; // A4 width in mm
    pdfHeight = 297; // A4 height in mm
  } else {
    pdfWidth = 216; // Letter width in mm
    pdfHeight = 279; // Letter height in mm
  }

  // Create PDF
  const pdf = new jsPDF({
    orientation,
    unit: 'px',
    format: pageSize === 'receipt' ? [pdfWidth, pdfHeight] : pageSize,
  });

  // Calculate position to center the receipt
  const x = pageSize === 'receipt' ? margin : (pdfWidth - imgWidth) / 2;
  const y = margin;

  // Add image to PDF
  pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

  return pdf.output('blob');
}

/**
 * Export and download a DOM element as PDF
 */
export async function downloadPDF(
  element: HTMLElement,
  options: PDFExportOptions = {}
): Promise<void> {
  const { filename = 'receipt.pdf' } = options;

  try {
    const blob = await exportToPDF(element, options);
    const url = URL.createObjectURL(blob);

    // Try programmatic download first
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Use a small delay to ensure the link is in the DOM
    await new Promise((resolve) => setTimeout(resolve, 10));
    
    link.click();
    
    // Clean up after a delay
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    // Fallback: if download doesn't work, open in new tab
    setTimeout(() => {
      // Check if download was blocked (this is a heuristic)
      if (document.body.contains(link)) {
        window.open(url, '_blank');
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }, 1000);
  } catch (error) {
    console.error('Failed to download PDF:', error);
    throw error;
  }
}

/**
 * Get PDF as data URL
 */
export async function getPDFDataURL(
  element: HTMLElement,
  options: PDFExportOptions = {}
): Promise<string> {
  const {
    scale = 2,
    backgroundColor = 'transparent',
    margin = 0,
    pageSize = 'receipt',
  } = options;

  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor,
    logging: false,
    useCORS: true,
    allowTaint: true,
    removeContainer: true,
    imageTimeout: 15000,
    onclone: (clonedDoc) => prepareReceiptForExport(clonedDoc, element),
  });

  const imgData = canvas.toDataURL('image/png', 1.0);
  const imgWidth = canvas.width / scale;
  const imgHeight = canvas.height / scale;

  let pdfWidth: number;
  let pdfHeight: number;

  if (pageSize === 'receipt') {
    pdfWidth = imgWidth + margin * 2;
    pdfHeight = imgHeight + margin * 2;
  } else if (pageSize === 'a4') {
    pdfWidth = 210;
    pdfHeight = 297;
  } else {
    pdfWidth = 216;
    pdfHeight = 279;
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: pageSize === 'receipt' ? [pdfWidth, pdfHeight] : pageSize,
  });

  const x = pageSize === 'receipt' ? margin : (pdfWidth - imgWidth) / 2;
  const y = margin;

  pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

  return pdf.output('datauristring');
}

